import { ButtonProps } from '@/components/ui/button/button';
import { env } from '@/config/env';

import type { HeroData } from '../../hooks';
import type { HeroStateCallbacks } from '../../utils/hero-state-parser';

import HERO_STATES_V2_OFFLINE, {
  DEFAULT_HERO_STATE_OFFLINE,
} from './hero-states-v2.offline';
import HERO_STATES_V2_ONLINE, {
  DEFAULT_HERO_STATE_ONLINE,
} from './hero-states-v2.online';

const isOffline = env.IS_OFFLINE;

export type Cta = {
  variant: ButtonProps['variant'];
  label: string;
  onClick: () => void | Promise<void>;
};

/**
 * Card layout used to render a state in the hero deck. New layouts register a
 * component under their type in HERO_CARD_REGISTRY (features/hero/components/
 * hero-cards) and add the type here.
 */
export type HeroCardType = 'default' | 'mathBeePromo' | 'bhanzuAppPromo';

export type HeroStateConfig = {
  id: string;
  name: string;

  // Deck behavior (hero carousel). Array order doubles as priority order.
  // Layout component for this state's card; defaults to 'default'.
  cardType?: HeroCardType;
  // When this is the highest-priority enabled state, it renders alone —
  // no carousel. For states that demand full attention (payments, live).
  exclusive?: boolean;
  // Only the first enabled state per group enters the deck, preserving the
  // mutual exclusion that first-match gave near-duplicate states.
  group?: string;
  // Enters the deck only when no non-fallback state is enabled.
  fallback?: boolean;

  // State condition checker
  isEnabled: (heroData: HeroData) => boolean;
  hasError?: (heroData: HeroData) => boolean;

  // Prop generators
  getTitle: (heroData: HeroData) => string;
  getSubtitle?: (heroData: HeroData) => string;
  getStats?: (heroData: HeroData) => React.ReactNode;
  getDivider?: (heroData: HeroData) => boolean;
  getOverButton?: (heroData: HeroData) => string;
  getCta?: (heroData: HeroData, callbacks?: HeroStateCallbacks) => Cta;
  getCharacterImage?: (heroData: HeroData) => string;
};

export const DEFAULT_HERO_STATE: HeroStateConfig = isOffline
  ? DEFAULT_HERO_STATE_OFFLINE
  : DEFAULT_HERO_STATE_ONLINE;

export const HERO_STATES_V2: HeroStateConfig[] = isOffline
  ? HERO_STATES_V2_OFFLINE
  : HERO_STATES_V2_ONLINE;
